import * as yup from "yup";
import {useMemo} from "react";
import {getI18n, useTranslation} from "react-i18next";
import {RequestI18N} from "@/translations";
import {CartonGradeDTO} from "@/api/data-contracts";
import {Constants} from "@/api/constants";
import {formatInteger, formatNumber, parseNumericString} from "@/lib/utils";
import {EDGE_CRUSH_UNIT, GSM} from "@/constants";
import {useCartonGrades} from "@/datacaches";

/**
 * Typ für die Validierung des CartonGradeDTO Objekts
 */
export type CartonGradeValidation = Pick<
    CartonGradeDTO,
    | "code"
    | "flute"
    | "outerLiner"
    | "fluteOne"
    | "linerOne"
    | "fluteTwo"
    | "linerTwo"
    | "fluteThree"
    | "innerLiner"
    | "weight"
    | "edgeCrushTest"
    | "wetStrength"
>;

/**
 * Custom hook that returns a Yup validation schema for the custom grade form
 */
export const useCustomGradeSchema = () => {
    const { t } = useTranslation();
    const locale = getI18n().language;
    const parseNumericStringWithLocale = parseNumericString(locale);
    const [cartonGrades, cartonGradesStatus] = useCartonGrades();

    return useMemo(() => {
        const schema: yup.ObjectSchema<any> = yup
            .object()
            .shape({
                // Quality / Grade - required, must be unique
                code: yup
                    .string()
                    .required(t(RequestI18N.grade.validation.qualityGradeRequired))
                    .min(
                        Constants.CARTON_GRADE_CODE_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_CODE_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_CODE_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_CODE_LENGTH_MAX, locale)
                        })
                    )
                    .test("unique-code", t(RequestI18N.grade.validation.qualityGradeUnique), function (value) {
                        if (!value || cartonGradesStatus !== "loaded") {
                            return true;
                        } // Skip validation if value is empty or data not loaded
                        // Check if the entered code matches any existing code in the cached grades
                        return !cartonGrades?.some((grade) => grade.values?.code === value);
                    }),

                // Flute - required
                flute: yup
                    .string()
                    .required(t(RequestI18N.grade.validation.fluteRequired))
                    .min(
                        Constants.CARTON_GRADE_FLUTE_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_FLUTE_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_FLUTE_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_FLUTE_LENGTH_MAX, locale)
                        })
                    ),

                // AD / o. liner - required
                outerLiner: yup
                    .string()
                    .required(t(RequestI18N.grade.validation.outerLinerRequired))
                    .min(
                        Constants.CARTON_GRADE_OUTER_LINER_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_OUTER_LINER_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_OUTER_LINER_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_OUTER_LINER_LENGTH_MAX, locale)
                        })
                    ),

                // W1 flute - required
                fluteOne: yup
                    .string()
                    .required(t(RequestI18N.grade.validation.fluteOneRequired))
                    .min(
                        Constants.CARTON_GRADE_FLUTE1_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_FLUTE1_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_FLUTE1_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_FLUTE1_LENGTH_MAX, locale)
                        })
                    ),

                // ZD 1 / liner - optional, but if provided, fluteTwo is required
                linerOne: yup
                    .string()
                    .nullable()
                    .transform((value) => (value === "" ? null : value))
                    .min(
                        Constants.CARTON_GRADE_LINER1_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_LINER1_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_LINER1_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_LINER1_LENGTH_MAX, locale)
                        })
                    ),

                // W2 flute - required if linerOne is provided
                fluteTwo: yup
                    .string()
                    .nullable()
                    .transform((value) => (value === "" ? null : value))
                    .min(
                        Constants.CARTON_GRADE_FLUTE2_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_FLUTE2_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_FLUTE2_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_FLUTE2_LENGTH_MAX, locale)
                        })
                    )
                    .when("linerOne", {
                        is: (linerOne: string | null) => linerOne != null && linerOne !== "",
                        then: (schema) => schema.required(t(RequestI18N.grade.validation.fluteTwoRequired))
                    }),

                // ZD 2 / liner - optional
                linerTwo: yup
                    .string()
                    .nullable()
                    .transform((value) => (value === "" ? null : value))
                    .min(
                        Constants.CARTON_GRADE_LINER2_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_LINER2_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_LINER2_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_LINER2_LENGTH_MAX, locale)
                        })
                    ),

                // W3 flute - optional
                fluteThree: yup
                    .string()
                    .nullable()
                    .transform((value) => (value === "" ? null : value))
                    .min(
                        Constants.CARTON_GRADE_FLUTE3_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_FLUTE3_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_FLUTE3_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_FLUTE3_LENGTH_MAX, locale)
                        })
                    ),

                // ID 1. liner - required
                innerLiner: yup
                    .string()
                    .required(t(RequestI18N.grade.validation.innerLinerRequired))
                    .min(
                        Constants.CARTON_GRADE_INNER_LINER_LENGTH_MIN,
                        t(RequestI18N.grade.validation.charMin, {
                            min: formatInteger(Constants.CARTON_GRADE_INNER_LINER_LENGTH_MIN, locale)
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_INNER_LINER_LENGTH_MAX,
                        t(RequestI18N.grade.validation.charMax, {
                            max: formatInteger(Constants.CARTON_GRADE_INNER_LINER_LENGTH_MAX, locale)
                        })
                    ),

                weight: yup
                    .number()
                    .nullable()
                    .optional()
                    .transform((value) => parseNumericStringWithLocale(value, value))
                    .required(t(RequestI18N.grade.validation.weightRequired))
                    .min(
                        Constants.CARTON_GRADE_WEIGHT_MIN,
                        t(RequestI18N.grade.validation.weightMin, {
                            min: formatNumber(Constants.CARTON_GRADE_WEIGHT_MIN, 2, locale),
                            unit: GSM
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_WEIGHT_MAX,
                        t(RequestI18N.grade.validation.weightMax, {
                            max: formatNumber(Constants.CARTON_GRADE_WEIGHT_MAX, 2, locale),
                            unit: GSM
                        })
                    ),

                // ECT Edge Crush - required
                edgeCrushTest: yup
                    .number()
                    .nullable()
                    .optional()
                    .transform((value) => parseNumericStringWithLocale(value, value))
                    .required(t(RequestI18N.grade.validation.edgeCrushTestRequired))
                    .min(
                        Constants.CARTON_GRADE_EDGE_CRUSH_TEST_MIN,
                        t(RequestI18N.grade.validation.edgeCrushTestMin, {
                            min: formatNumber(Constants.CARTON_GRADE_EDGE_CRUSH_TEST_MIN, 2, locale),
                            unit: EDGE_CRUSH_UNIT
                        })
                    )
                    .max(
                        Constants.CARTON_GRADE_EDGE_CRUSH_TEST_MAX,
                        t(RequestI18N.grade.validation.edgeCrushTestMax, {
                            max: formatNumber(Constants.CARTON_GRADE_EDGE_CRUSH_TEST_MAX, 2, locale),
                            unit: EDGE_CRUSH_UNIT
                        })
                    ),

                // Wet Strength - optional
                wetStrength: yup.boolean().default(false)
            })
            .stripUnknown();

        const typed = schema as yup.ObjectSchema<CartonGradeValidation>;
        return typed as yup.ObjectSchema<CartonGradeDTO>;
    }, [t, locale, cartonGradesStatus, cartonGrades, parseNumericStringWithLocale]);
};
